import React, { useEffect } from "react";
import { captureException, wrapCreateBrowserRouter } from "@sentry/react";
import { Teams } from "./Containers/Teams";
import { UserResponse } from "./Containers/UserResponse";
import { Templates } from "./Containers/Templates";
import { EditTemplate } from "./Containers/EditTemplate";
import { Surveys } from "./Containers/Surveys";
import { Assignments } from "./Containers/Assignments";
import { EditCampaign } from "./Containers/EditCampaign";
import { Results } from "./Containers/Results";
import { PreviewTemplate } from "./Containers/PreviewTemplate";
import { OpenResponses } from "./Containers/OpenResponses";
import { ResultsAnon } from "./Containers/ResultsAnon";
import { ResultsPreview } from "./Containers/ResultsPreview";
import { OAuth } from "./Containers/OAuth";
import { ErrorMessage } from "./Containers/ErrorMessage";
import { SiteDashboard } from "./Containers/SiteDashboard";
import {
  createBrowserRouter,
  useNavigate,
  useRouteError,
  Navigate,
} from "react-router-dom";
import { useQuery } from "@apollo/client";
import { GET_ROLE } from "./Queries";
import { UserAssignmentSubmission } from "./Containers/UserAssignmentSubmission";

function ErrorHandler() {
  const routeError = useRouteError();

  useEffect(() => {
    captureException(routeError, { level: "fatal" });
  }, [routeError]);

  return <ErrorMessage />;
}

// Protected route component for admin-only routes
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { data: roleData } = useQuery(GET_ROLE);
  const isInstanceAdmin = roleData?.isInstanceAdmin;

  if (!isInstanceAdmin) {
    return <Navigate to="/" />;
  }

  return <>{children}</>;
}

const universalRoutes = [
  {
    path: "/oauth/redirect",
    element: <OAuth />,
  },
];

export const siteNavigationRouter = wrapCreateBrowserRouter(
  createBrowserRouter,
)([
  {
    path: "/",
    errorElement: <ErrorHandler />,
    children: [
      {
        path: "/",
        element: <Navigate to="/dashboard" />,
      },
      {
        path: "dashboard",
        element: <SiteDashboard />,
      },
      // All other paths will redirect to dashboard
      {
        path: "*",
        element: <Navigate to="/dashboard" />,
      },
    ],
  },
]);

export const instructorRouter = wrapCreateBrowserRouter(createBrowserRouter)([
  {
    path: "/",
    errorElement: <ErrorHandler />,
    children: [
      ...universalRoutes,
      {
        path: "/",
        element: <Navigate to="/teams" />,
      },
      {
        path: "teams",
        element: <Teams />,
      },
      {
        path: "templates",
        element: <Templates />,
      },
      {
        path: "templates/:templateId",
        element: <EditTemplate />,
      },
      {
        path: "surveys",
        element: <Surveys />,
      },
      {
        path: "assignments",
        element: <Assignments />,
      },
      {
        path: "campaigns/:campaignId",
        element: <EditCampaign />,
      },
      {
        path: "results/preview/:campaignId/:userId/:source",
        element: <ResultsPreview />,
      },
      {
        path: "results/:campaignId/:subjectType?/:teamId?/:userId?",
        element: <Results />,
      },
      {
        path: "results/:campaignId/:subjectType?//:userId?",
        element: <Results />,
      },
      {
        path: "results",
        element: <ResultsAnon />,
      },
      {
        path: "preview-template/:templateId",
        element: <PreviewTemplate />,
      },
      {
        path: "response",
        element: <UserResponse />,
      },
      {
        path: "assignment-submission",
        element: <UserAssignmentSubmission />,
      },
      {
        path: "dashboard",
        element: (
          <AdminRoute>
            <SiteDashboard />
          </AdminRoute>
        ),
      },
    ],
  },
]);

export const studentRouter = createBrowserRouter([
  {
    path: "/",
    errorElement: <ErrorHandler />,
    children: [
      ...universalRoutes,
      {
        path: "/",
        element: <Navigate to="/surveys" />,
      },
      {
        path: "surveys",
        element: <OpenResponses />,
      },
      {
        path: "assignments",
        element: <OpenResponses />,
      },
      {
        path: "results",
        element: <ResultsAnon />,
      },
      {
        path: "response",
        element: <UserResponse />,
      },
      {
        path: "assignment-submission",
        element: <UserAssignmentSubmission />,
      },
    ],
  },
]);
